import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { adminService } from '../services/adminApi';
import { Search, RotateCcw, Pen, AlertCircle, CheckCircle, Loader2, X, Save, Trash2, Music, File, Upload } from 'lucide-react';

const SongManager = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Edit State
  const [editingSong, setEditingSong] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Handle Escape Key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setEditingSong(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = async (e) => {
    const val = e.target.value;
    setQuery(val);
    
    // Clear message on new search
    if (message) setMessage(null);

    if (val.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      // Admins use the same search engine as members
      const data = await adminService.searchSongs(val);
      setResults(data);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.status === 401 
        ? 'Unauthorized. Please sign in or provide the rehearsal password.' 
        : 'Search failed. Backend might be down or database is disconnected.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (song) => {
    if (!window.confirm(`Are you sure you want to REVERT '${song.title}' to its original state? This will overwrite all manual changes.`)) return;
    
    try {
      await adminService.revertToOriginal(song.song_slug);
      setMessage({ type: 'success', text: `Successfully reverted '${song.title}'` });
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to revert '${song.title}'` });
    }
  };

  const handleEditClick = (song) => {
    setEditingSong(song);
    setEditForm({
      title: song.title,
      collection: song.collection || '',
      track_number: song.track_number || '',
      arranged_by: song.arranged_by || '',
      admin_notes: song.admin_notes || ''
    });
  };

  const handleDeleteMedia = async (fileUrl) => {
    if (!window.confirm('Are you sure you want to delete this file? This cannot be undone.')) return;
    
    try {
      await adminService.deleteMediaFromSong(editingSong.song_slug, fileUrl);
      
      // Update local state
      const updatedMedia = editingSong.media_files.filter(m => m.url !== fileUrl);
      const updatedSong = { ...editingSong, media_files: updatedMedia };
      
      setEditingSong(updatedSong);
      setResults(prev => prev.map(s => s.song_slug === editingSong.song_slug ? updatedSong : s));
      
      setMessage({ type: 'success', text: 'File deleted successfully.' });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to delete file.' });
    }
  };

  const handleDeleteSong = async () => {
    if (!editingSong) return;
    if (!window.confirm(`Are you sure you want to delete '${editingSong.title}'? This will hide it from the public API.`)) return;

    setMessage({ type: 'info', text: 'Deleting song...' });
    try {
      await adminService.deleteSong(editingSong.song_slug);
      setMessage({ type: 'success', text: `Deleted '${editingSong.title}' successfully.` });
      
      // Remove from list
      setResults(prev => prev.filter(s => s.song_slug !== editingSong.song_slug));
      setEditingSong(null);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to delete song.' });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingSong) return;
    
    // 100MB Limit Check
    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      alert(`File size exceeds the 100MB limit. Your file is ${(file.size / (1024*1024)).toFixed(2)}MB.`);
      e.target.value = null;
      return;
    }

    setUploadingMedia(true);
    try {
      // 1. Proxy Upload
      const uploadRes = await adminService.proxyUpload(file);
      
      // 2. Add Media to DB
      const category = file.type.includes('pdf') ? 'score' : 'audio';
      const type = file.type.split('/')[1] || 'unknown';
      
      await adminService.addMediaToSong(editingSong.song_slug, {
        category,
        type,
        label: file.name,
        url: uploadRes.url,
        voice_part: null
      });

      // 3. Update local state
      const newMedia = {
        category,
        type,
        label: file.name,
        url: uploadRes.url,
        voice_part: null
      };

      const updatedSong = { ...editingSong, media_files: [...(editingSong.media_files || []), newMedia] };
      setEditingSong(updatedSong);
      setResults(prev => prev.map(s => s.song_slug === editingSong.song_slug ? updatedSong : s));
      
      setMessage({ type: 'success', text: `Uploaded '${file.name}' successfully.` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to upload media.' });
    } finally {
      setUploadingMedia(false);
      // reset file input
      e.target.value = null;
    }
  };

  const handleSaveEdit = async () => {
    if (!editingSong) return;
    
    // Optimistic UI update or wait for server?
    // Let's wait for server to ensure data integrity
    setMessage({ type: 'info', text: 'Saving changes...' });

    try {
      await adminService.updateSongMetadata(editingSong.song_slug, {
        ...editForm,
        track_number: editForm.track_number ? parseInt(editForm.track_number) : null
      });

      setMessage({ type: 'success', text: `Updated '${editForm.title}'` });
      
      // Update local list
      setResults(prev => prev.map(s => {
        if (s.song_slug === editingSong.song_slug) {
          return {
            ...s,
            ...editForm,
            track_number: editForm.track_number ? parseInt(editForm.track_number) : null
          };
        }
        return s;
      }));

      setEditingSong(null);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to update song. Please try again.' });
    }
  };

  return (
    <div className="space-y-8 animate-slide-up">
      {/* Search Section */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 relative overflow-hidden group">
        {/* Decorative Blob */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl -translate-y-32 translate-x-32 opacity-50 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Manage Archive Content</h2>
          <p className="text-gray-500 mb-6">Search and modify the choir's digital song repository.</p>

          <div className="relative shadow-md rounded-2xl transition-all duration-300 focus-within:shadow-xl focus-within:-translate-y-1 bg-white">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
              {loading ? <Loader2 className="animate-spin text-blue-500" size={24}/> : <Search size={24} />}
            </div>
            <input
              type="text"
              placeholder="Search by title, lyrics, or hymn number..."
              className="w-full pl-12 pr-4 py-4 bg-transparent border-none rounded-2xl focus:ring-0 text-lg text-gray-800 placeholder-gray-400"
              value={query}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      {message && (
        <div 
          className={`p-4 rounded-2xl flex items-center gap-3 text-sm font-bold shadow-sm animate-fade-in border ${
            message.type === 'success' 
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
              : message.type === 'info'
                ? 'bg-blue-50 text-blue-700 border-blue-100'
                : 'bg-red-50 text-red-700 border-red-100'
          }`}
        >
          <div className={`p-2 rounded-full ${message.type === 'success' ? 'bg-emerald-100' : 'bg-red-100'}`}>
            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          </div>
          <span>{message.text}</span>
        </div>
      )}

      {/* Results Section */}
      <div className="space-y-4">
        {query.length > 0 && results.length === 0 && !loading ? (
          <div className="text-center py-12 opacity-50">
            <Search size={48} className="mx-auto mb-4 text-gray-300"/>
            <p className="text-lg text-gray-500">No songs found matching "{query}"</p>
          </div>
        ) : (
          results.map((song) => (
            <div 
              key={song.song_slug} 
              onClick={() => handleEditClick(song)}
              className="group p-5 rounded-2xl border transition-all duration-300 flex items-center justify-between cursor-pointer bg-white border-gray-100 shadow-sm hover:shadow-lg hover:border-blue-100"
            >
              <div className="flex items-center gap-4 overflow-hidden">
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg truncate group-hover:text-blue-700 transition-colors">
                    {song.title}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs font-bold text-gray-600 uppercase tracking-wide">
                      {song.collection} {song.track_number}
                    </span>
                    <span className="font-mono text-xs opacity-50 truncate hidden sm:inline-block">
                      {song.song_slug}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pl-4 flex-shrink-0">
                <button 
                  onClick={(e) => { e.stopPropagation(); handleRevert(song); }}
                  className="p-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-xl transition-colors"
                  title="Revert to original"
                >
                  <RotateCcw size={18} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleEditClick(song); }}
                  className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                  title="Edit Metadata"
                >
                  <Pen size={18} />
                </button>
              </div>
            </div>
          ))
        )}
        
        {results.length === 0 && !query && (
          <div className="text-center py-20">
            <p className="text-gray-400">Start typing to search the repository...</p>
          </div>
        )}
      </div>

      {/* Edit Modal (Portal) */}
      {editingSong && createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in cursor-pointer"
          onClick={() => setEditingSong(null)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slide-up ring-1 ring-gray-900/5 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center bg-gray-50/50">
              <h3 className="text-xl font-bold text-gray-900">Edit Song Metadata</h3>
              <button onClick={() => setEditingSong(null)} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Title</label>
                <input 
                  value={editForm.title}
                  onChange={e => setEditForm({...editForm, title: e.target.value})}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-semibold text-gray-900"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Collection</label>
                  <input 
                    value={editForm.collection}
                    onChange={e => setEditForm({...editForm, collection: e.target.value})}
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium"
                    placeholder="e.g. VIP"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Track #</label>
                  <input 
                    type="number"
                    value={editForm.track_number}
                    onChange={e => setEditForm({...editForm, track_number: e.target.value})}
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium"
                    placeholder="e.g. 171"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Arranged By</label>
                <input 
                  value={editForm.arranged_by}
                  onChange={e => setEditForm({...editForm, arranged_by: e.target.value})}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium"
                  placeholder="e.g. John Doe"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Admin Notes</label>
                <textarea 
                  value={editForm.admin_notes}
                  onChange={e => setEditForm({...editForm, admin_notes: e.target.value})}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all font-medium min-h-[80px]"
                  placeholder="Internal notes..."
                />
              </div>

              {/* Media Files Section */}
              <div className="pt-2 border-t border-gray-100">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Media Files</label>
                  <div>
                    <input 
                      type="file" 
                      id="media-upload" 
                      className="hidden" 
                      onChange={handleFileUpload} 
                      accept=".pdf,.mp3,.wav,.m4a"
                      disabled={uploadingMedia}
                    />
                    <label 
                      htmlFor="media-upload" 
                      className={`flex items-center gap-1 px-3 py-1 text-xs font-bold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors ${uploadingMedia ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingMedia ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      Add Media
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  {editingSong.media_files?.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-xl hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all group">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className={`p-2 rounded-lg border ${file.category === 'scores' || file.category === 'score' ? 'bg-red-50 text-red-500 border-red-100' : 'bg-blue-50 text-blue-500 border-blue-100'}`}>
                          {file.category === 'scores' || file.category === 'score' ? <File size={16} /> : <Music size={16} />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate" title={file.label || file.name}>
                            {file.label || file.name}
                          </p>
                          <a href={file.url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline truncate block max-w-[200px]">
                            View File
                          </a>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleDeleteMedia(file.url)}
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                        title="Delete File"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {(!editingSong.media_files || editingSong.media_files.length === 0) && (
                    <div className="text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <p className="text-sm text-gray-400 italic">No media files attached.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-between gap-3 shrink-0">
              <button 
                onClick={handleDeleteSong}
                className="px-5 py-2.5 font-bold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-xl transition-all flex items-center gap-2"
                title="Soft Delete Song"
              >
                <Trash2 size={18} />
                Delete Song
              </button>
              <div className="flex gap-3">
                <button 
                  onClick={() => setEditingSong(null)} 
                  className="px-5 py-2.5 font-bold text-gray-600 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSaveEdit} 
                className="px-6 py-2.5 font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors flex items-center gap-2 shadow-lg shadow-blue-200"
              >
                <Save size={18} /> Save Changes
              </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SongManager;
